import { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Paper } from "@mui/material";
import { toast } from "react-toastify";
import { Button } from "Atoms";
import AttachmentManager from "Atoms/AttachmentManager";
import DeletePopUp from "Components/DeletePopUp";
import { deleteAttachmentsAPI } from "Zustand/services/attachmentService";
import { getTestSpecificationByNodeIdAPI } from "Zustand/services/projectsService";
import { useBoundStore } from "Zustand/store/mainStore";
import { getAttachmentChanges, PDF_EXTS } from "Utils/helperFunctions";

// Local attachment shape, matching the pattern already used for Test Plan
// approval documents and Test Report attachments (no shared module exists).
type AttachmentAtom = {
  id: string;
  title: string;
  description: string;
  file?: File;
  url?: string;
  mimetype?: string;
  nodeid?: string;
  specifier?: string;
};

type Props = {
  testSpecificationNodeId: string;
};

export default function Attachments({ testSpecificationNodeId }: Props) {
  const { uploadAttachments } = useBoundStore();

  const [loading, setLoading] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<
    AttachmentAtom[]
  >([]);
  const [newUploads, setNewUploads] = useState<AttachmentAtom[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AttachmentAtom | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  // Existing (already-uploaded) attachments + not-yet-uploaded ones, shown as
  // a single manager instead of two separate "uploaded"/"upload" panels.
  const combinedAttachments: AttachmentAtom[] = [
    ...existingAttachments,
    ...newUploads,
  ];

  const loadAttachments = useCallback(async () => {
    if (!testSpecificationNodeId) return;
    setLoading(true);
    try {
      const response = await getTestSpecificationByNodeIdAPI(
        testSpecificationNodeId
      );
      const testspecification = response?.testspecification ?? response;

      const attachments: AttachmentAtom[] = Array.isArray(
        testspecification?.hasattachment
      )
        ? testspecification.hasattachment.map((item: any) => {
            const attachment = item?.attachment ?? item;
            return {
              id: attachment?.file?.fileid ?? attachment?.nodeid ?? "",
              title:
                attachment?.title ||
                attachment?.file?.fileid?.split("/").pop() ||
                "untitled",
              description: attachment?.description || "",
              url: attachment?.file?.url,
              mimetype: attachment?.file?.mimetype,
              nodeid: attachment?.nodeid,
              specifier: attachment?.specifier || "",
            };
          })
        : [];

      setExistingAttachments(attachments);
    } catch (e) {
      console.error("Failed to load test specification attachments", e);
    } finally {
      setLoading(false);
    }
  }, [testSpecificationNodeId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleAttachmentsChange = useCallback(
    async (updated: AttachmentAtom[]) => {
      const { toEdit, toDelete } = getAttachmentChanges(
        combinedAttachments,
        updated
      );

      // Existing items never carry a `file` (disableFileChangeForExisting
      // blocks replacing them), so this isolates newly staged uploads.
      setNewUploads(updated.filter((item) => item.file));

      if (toDelete.length > 0) {
        // Don't delete yet — the item stays visible (it's re-derived from
        // `existingAttachments`, untouched above) until the confirm dialog
        // resolves.
        setDeleteTarget(toDelete[0] as AttachmentAtom);
      }

      if (toEdit.length > 0) {
        try {
          await uploadAttachments(testSpecificationNodeId, [], toEdit);
          toast.success("Attachment updated.");
          await loadAttachments();
        } catch (e: any) {
          console.error("Failed to update attachment", e);
          toast.error(e?.message || "Failed to update attachment.");
        }
      }
    },
    [combinedAttachments, uploadAttachments, testSpecificationNodeId, loadAttachments]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget?.nodeid) {
      setDeleteTarget(null);
      return;
    }

    setDeleting(true);
    try {
      await deleteAttachmentsAPI([deleteTarget.nodeid]);
      toast.success("Attachment deleted.");
      setDeleteTarget(null);
      await loadAttachments();
    } catch (e: any) {
      console.error("Failed to delete attachment", e);
      toast.error(e?.message || "Failed to delete attachment.");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, loadAttachments]);

  const handleUpload = useCallback(async () => {
    const uploadable = newUploads.filter((attachment) => attachment.file);

    if (!uploadable.length) {
      toast.error("Please choose a document to upload.");
      return;
    }

    setUploading(true);
    try {
      await uploadAttachments(testSpecificationNodeId, uploadable, []);
      setNewUploads([]);
      toast.success("Attachment uploaded.");
      await loadAttachments();
    } catch (e: any) {
      console.error("Failed to upload attachment", e);
      toast.error(e?.message || "Failed to upload attachment.");
    } finally {
      setUploading(false);
    }
  }, [newUploads, uploadAttachments, testSpecificationNodeId, loadAttachments]);

  return (
    <Box>
      <Paper sx={{ p: 3 }} elevation={3}>
        {loading && <CircularProgress />}

        <AttachmentManager
          _attachments={combinedAttachments}
          onChange={handleAttachmentsChange}
          disableFileChangeForExisting
          defaultExpanded
          allowedFileExtensions={PDF_EXTS}
          cropFeature={false}
          downloadAble
          title="Attachments"
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!newUploads.length || uploading}
          >
            {uploading ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            Upload
          </Button>
        </Box>

        <DeletePopUp
          open={!!deleteTarget}
          handleClose={() => !deleting && setDeleteTarget(null)}
          handleDelete={handleConfirmDelete}
        />
      </Paper>
    </Box>
  );
}
